import { ChangeEvent } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Close, CloudUploadOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";

interface FileUploadFieldProps {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const FileUploadField = ({ file, onChange, disabled = false }: FileUploadFieldProps) => {
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error("File size must be below 5MB");
      event.target.value = "";
      return;
    }

    if (!selectedFile.type.startsWith("image/") && selectedFile.type !== "application/pdf") {
      toast.error("Only images and PDF files are allowed");
      event.target.value = "";
      return;
    }

    onChange(selectedFile);
    event.target.value = "";
  };

  return (
    <Box mt={2}>
      <Typography variant="subtitle2" mb={1}>
        Attachments
      </Typography>

      <Box
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          border: "2px dashed",
          borderColor: "divider",
          borderRadius: 2,
          p: 2,
          cursor: disabled ? "not-allowed" : "pointer",
          color: disabled ? "text.disabled" : "primary.main",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <CloudUploadOutlined />

        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {file ? "Click to change attachment" : "Click to select a document"}
        </Typography>

        <input type="file" hidden accept="image/*,application/pdf" onChange={handleFileChange} disabled={disabled} />
      </Box>

      {file && (
        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {file.name}
          </Typography>

          <IconButton size="small" aria-label="Remove attachment" onClick={() => onChange(null)} disabled={disabled}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        Supported formats: PDF, Image (Max 5MB)
      </Typography>
    </Box>
  );
};
